import { FilterCard, CustomDataTable, useApi, Toaster, CustomAddPersonnelModal, CustomBackdrop, validationMessages } from '../../utils/globalImports';
import { React, useState, useEffect,} from '../../utils/globalImports';
import { ApiResponse } from '../../services/apiService';
import { useConfirmCloseDialog } from '../../contexts/ConfirmCloseDialogContext';

interface Personnel {
    personnelId?: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNo: string;
    userTypes: number[];
    status: boolean;
    description: string;
    createdId?: number;
    updatedId?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

const userTypeMap: Record<number, string> = {
    1: "Technical Project Lead",
    2: "Assistant Technical Project Lead",
};

const Personnels: React.FC = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [personnelList, setPersonnelList] = useState<Personnel[]>([]);
    const [selectedPersonnel, setSelectedPersonnel] = useState<any | null>(null);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [currentStatus, setCurrentStatus] = useState<string[]>(['Active']);
    const [personnelData, setPersonnelData] = useState<any[]>([]);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [isOn, setIsOn] = useState<boolean>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

    const { execute, loading } = useApi();
    const { showConfirmCloseDialog } = useConfirmCloseDialog();
    const props = { showUserTypeDropdown: true, showClientDropdown: false, title: " ", actionText: 'Add Personnel', searchbarText: validationMessages.personnel_searchText };

    const userId = Number(localStorage.getItem("userId"));
    
    useEffect(() => {
        fetchPersonnel();
    },[]);

    const fetchPersonnel = async () => {
        const { data } = await execute<Personnel[]>('get', '/Lookup/GetAllPersonnel');
    
        if (data) {
            setPersonnelList(data); 
    
            const personnelData = data.map((personnel) => {
                const { personnelId, firstName, lastName, email, userTypes, status, phoneNo} = personnel;
    
                const Name = `${firstName} ${lastName}`.trim(); 
                const UserType = userTypes.length > 0
                    ? userTypes.map(id => userTypeMap[id] || `Unknown (${id})`).join(' , ')
                    : "No User Types Assigned";
                const Status = status ? 'Active' : 'Inactive';
                return { 
                    id: personnelId, 
                    userTypeId: userTypes,
                    phoneNo,  
                    Name: Name,  
                    Email: email,  
                    "User Types": UserType,  
                    Status 
                };
            });
            setPersonnelData(personnelData);
            // Filter for status = 1 (Active) and set to filteredData
            const activePersonnel = personnelData
                .filter((client) => currentStatus.includes(client.Status))
                .sort((a, b) =>
                    a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' }) ||
                    a.Email.localeCompare(b.Email, undefined, { sensitivity: 'base' })
                );
            setFilteredData(activePersonnel);
        }
    };      

    const handleSearchUserData = (query: string, status: Number, userType?: number) => {
        const lowerCaseQuery = query.toLowerCase();

        // Map status strings to corresponding numbers
        const statusMapping: Record<string, number> = {
            Active: 1,
            Inactive: 0,
        };

        const filtered = personnelData.filter((item) => {

            // Convert item.Status to its numeric equivalent or skip if it doesn't match
            const itemStatus = statusMapping[item.Status] ?? -1;

            const matchesQuery =
                item.Name.toLowerCase().includes(lowerCaseQuery) ||
                (item.Email.toLowerCase().includes(lowerCaseQuery));

            const matchUserType = userType === -1 || item.userTypeId.some((id: number) => id === userType);

            const matchesStatus =
                status === -1 || itemStatus === status;

            return matchesQuery && matchUserType && matchesStatus;
        })
            .sort((a, b) =>
                a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' }) ||
                a.Email.localeCompare(b.Email, undefined, { sensitivity: 'base' })
            );
        setFilteredData(filtered);
        setCurrentStatus(
            status === 0
                ? ['Inactive'] // When status is 0, set currentStatus to 'Inactive'
                : status === -1
                    ? ['Active', 'Inactive'] // When status is -1 or 2, set currentStatus to both 'Active' and 'Inactive'
                    : status === 1
                        ? ['Active'] // When status is 1, set currentStatus to 'Active'
                        : currentStatus // Fallback to the current value if no conditions are met
        );
    };
    
    const handleFormSubmit = async (formData: any, setBackendErrors?: (errors: any) => void) => {
        let response: ApiResponse<any> = { data: null, error: null };

        if (selectedPersonnel) {

            const updatedPersonnel = personnelList.find((personnel) => personnel.personnelId === selectedPersonnel.personnelId);
            // If the personnel is found, update the fields
            if (updatedPersonnel) {
                // Step 2: Update the fields on the filtered record
                const updatedPersonnelData = {
                    ...updatedPersonnel, // Keep the original personnel data
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    phoneNo: formData.phoneNo,
                    description: formData.description,
                    status: formData.status === 'Active' ? true : false, 
                    updatedId: userId,
                    userTypes: formData.userType,
                };
            response = await execute('put', '/Lookup/UpdatePersonnel', updatedPersonnelData);
            }
        } else {
            // Create new personnel
            const newPersonnel: Personnel = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phoneNo: formData.phoneNo,
                description: formData.description,
                status: formData.status === 'Active' ? true : false,
                createdId: userId,
                userTypes: formData.userType, 
            };
    
            response = await execute('post', '/Lookup/CreatePersonnel', newPersonnel);
        }
    
        if (response && response.error) {
            if (
              typeof response.error === 'string' &&
              response.error.includes('Email already exists')
            ) {
              setBackendErrors?.({ email: response.error });
              return false; // Don't close modal
            } else {
              setToast({ open: true, message: response.error, type: 'error' });
              return false; // Don't close modal on other errors
            }
          } else {
            // Submission successful – close the modal
            setToast({ open: true, message: response?.data, type: 'success' });
            setModalOpen(false);
            setSelectedPersonnel(null);
            handleContentChange(false);
            fetchPersonnel();
            return true;
          }
    };    
    
    const handleAdd = () => {
        setSelectedPersonnel(null);
        setModalOpen(true);
    };

    const handleEdit = (personnel: any) => {
        setSelectedPersonnel({
            personnelId: personnel.id,
            firstName: personnel.Name.split(" ")[0] || "", // Extract first name
            lastName: personnel.Name.split(" ")[1] || "",  // Extract last name
            email: personnel.Email, 
            phoneNo: personnel.phoneNo,
            userType : personnel.userTypeId,
            description: personnel.description || "",
            status : personnel.Status,
        });
        setModalOpen(true);
    };

    const handleToggleState = (row: any) => {
        if (row.Status === 'Inactive') {
            setIsOn(false)
        } else {
            setIsOn(true)
        }
        setSelectedPersonnel(row);
        setDialogOpen(true);
    };

    const handleDialogConfirm = async () => {
        if (!selectedPersonnel) return;
        try {
            // Find the selected personeel in the list
            const updatedPersonnel = personnelList.find(
                (personnel) => personnel.personnelId === selectedPersonnel.id
            );
            if (updatedPersonnel) {
                //Toggle the status dynamically
                const newStatus = updatedPersonnel.status ? false : true;
                const updatedPersonnelData = {
                    ...updatedPersonnel,
                    Status: newStatus, //Toggle between true (Active) and false (Inactive)
                    updatedId: userId,
                };
    
                //Make the API call to update the status
                const response = await execute('put', '/Lookup/UpdatePersonnel', updatedPersonnelData);
                if (response?.error) {
                    setToast({ open: true, message: response.error, type: 'error' });
                  } else {
                    setToast({ open: true, message: 'Status updated successfully', type: 'success' });
                  }
            }
    
            //Refresh the tag list
            fetchPersonnel();
            setSelectedPersonnel(null);
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setDialogOpen(false);
        }
    };    

    const handleContentChange = (state:boolean) => {
    setHasUnsavedChanges(state); 
    };

  const handleClose=()=>{
    if (hasUnsavedChanges) { 
        showConfirmCloseDialog({
          onConfirm: () => { 
            setHasUnsavedChanges(false);  
            setModalOpen(false);       
          },
          onCancel: () => { 
          },
        });
      }
      else{
        setModalOpen(false);
      } 
  }

    return (
        <>
            <FilterCard {...props} handleModalOrNavigate={handleAdd} handleSearch={handleSearchUserData}/>
            <CustomAddPersonnelModal handleContentChange={handleContentChange} open={modalOpen} onClose={handleClose} onSubmit={handleFormSubmit} personnelData={selectedPersonnel} />
            {personnelList.length > 0 && (
                <CustomDataTable
                    rows={filteredData}
                    actionText="Personnel"
                    sortOptions={[{ value: "Name", text: "Name" }, { value: "Email", text: "Email" }]}
                    onRowEdit={handleEdit}
                    onToggleClick={handleToggleState}
                    isDialogOpen={dialogOpen}
                    isToggleOn={isOn}
                    handleCancel={() => setDialogOpen(false)}
                    handleConfirm={handleDialogConfirm}
                    // gridHeight={'calc(100vh - 500px)'}
                    noResultMessage={validationMessages.common_RecordNotFound("personnels")}
                />)}
            <CustomBackdrop open={loading} />
            <Toaster open={toast.open} message={toast.message || ""} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
        </>
    );
};

export default Personnels;